import { Card, CardContent, CardHeader } from '@/components/ui/card'
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select'
import { formatCFA } from '@/lib/utils'
import type { BarDatumNV } from '@/types/backend'
import { ResponsiveBar } from '@nivo/bar'

const MONTH_LABELS = [
  'Janvier',
  'Février',
  'Mars',
  'Avril',
  'Mai',
  'Juin',
  'Juillet',
  'Août',
  'Septembre',
  'Octobre',
  'Novembre',
  'Décembre',
]

type BilanEncaissementsJournalierProps = {
  data: BarDatumNV[]
  month: number
  year: number
  onMonthChange: (month: number) => void
  gridColor: string
  gridOpacity: number
  emptyMsg?: string
}

const BilanEncaissementsJournalier: React.FC<
  BilanEncaissementsJournalierProps
> = ({
  data,
  month,
  year,
  onMonthChange,
  gridColor,
  gridOpacity,
  emptyMsg = 'Aucun encaissement sur ce mois.',
}) => {
  // data is zero-filled by the caller for every day of the month, so
  // data.length alone can't tell us "empty" — check the values instead.
  const hasData = data.some((d) => Number(d.value) > 0)
  const safeData = data.length ? data : [{ name: '', value: 0 }]

  return (
    <Card className='min-h-[360px]'>
      <CardHeader>
        <div className='flex flex-wrap items-center justify-between gap-2'>
          <h4 className='text-lg font-medium'>Encaissements journaliers</h4>
          <Select
            value={String(month)}
            onValueChange={(v) => onMonthChange(Number(v))}
          >
            <SelectTrigger className='w-[140px]'>
              <SelectValue placeholder='Mois' />
            </SelectTrigger>
            <SelectContent>
              {MONTH_LABELS.map((label, i) => (
                <SelectItem key={label} value={String(i + 1)}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent className='h-[300px]'>
        <ResponsiveBar<BarDatumNV>
          data={safeData}
          keys={['value']}
          indexBy='name'
          margin={{ top: 10, right: 10, bottom: 40, left: 48 }}
          padding={0.3}
          borderRadius={6}
          axisBottom={{ tickPadding: 12, tickRotation: -30 }}
          axisLeft={{ tickPadding: 6 }}
          enableLabel={false}
          theme={{
            grid: { line: { stroke: gridColor, strokeOpacity: gridOpacity } },
            tooltip: { container: { fontSize: 12, borderRadius: 8 } },
          }}
          colors={['#16a34a']}
          tooltip={({ indexValue, value }) => (
            <div className='rounded-md border dark:bg-primary bg-white p-2 text-xs shadow'>
              <div>
                Jour:{' '}
                <b>
                  {String(indexValue)}/{String(month).padStart(2, '0')}/{year}
                </b>
              </div>
              <div>
                Encaissements: <b>{formatCFA(Number(value))}</b>
              </div>
            </div>
          )}
        />
        {!hasData && (
          <div className='mt-2 text-center text-xs text-muted-foreground'>
            {emptyMsg}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export default BilanEncaissementsJournalier
